/**
 * Hooks
 */
import React, { useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Contexts
 */
import { useToolsContext } from '../../../context/ToolsContext';
import MapContext from '../../Map/MapContext';

/**
 * Components
 */
import Tool from '../../Tool/Tool';
import { Draw } from 'ol/interaction';
import { sourceMeasurePolygon, styleFunction, modifyStyle, tipPoint, modify, vectorMeasurePolygon } from './measureFunctions';

function MeasurePolygon() {
    // const [isActive, setIsActive] = useState(false);
    const { toolStates, setToolState } = useToolsContext();
    const { map } = useContext(MapContext); // instancia del mapa del contexto
    const { t } = useTranslation();

    const drawRef = useRef(null);

    /**
     * Ensure the tool layer is above others
     */
    const ensureLayerZIndex = () => {
        if (!vectorMeasurePolygon || !map) return;
        vectorMeasurePolygon.setZIndex(999);   // Set a high z-index
        map.removeLayer(vectorMeasurePolygon); // Remove if already added
        map.addLayer(vectorMeasurePolygon);    // Re-add to ensure it's on top
    }

    function addInteraction(drawType) {
        if (!map) return;

        const activeTip = t('measureTool.tooltip') || 'Click para continuar medición';
        const idleTip = '';
        let tip = idleTip;

        const draw = new Draw({
            source: sourceMeasurePolygon,
            type: drawType,
            style: function (feature) {
                return styleFunction(feature, true, drawType, tip);
            }
        });

        draw.on('drawstart', function () {
            sourceMeasurePolygon.clear();
            modify.setActive(false);
            tip = activeTip;
        });

        draw.on('drawend', function () {
            modifyStyle.setGeometry(tipPoint);
            modify.setActive(true);
            map.once('pointermove', function () {
                modifyStyle.setGeometry();
            });
            tip = idleTip;
        });

        // Guardar en ref
        drawRef.current = draw;

        // Eventos para borrar el dibujo con Esc
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                // Borrar completamente el dibujo actual
                sourceMeasurePolygon.clear();
                // Reiniciar la interacción de dibujo
                if (drawRef.current) {
                    map.removeInteraction(drawRef.current);
                    map.addInteraction(drawRef.current);
                }
            }
        };

        const handleContextMenu = (e) => {
            e.preventDefault();
            // Cancelar el dibujo actual
            if (drawRef.current) drawRef.current.finishDrawing();
        };

        window.addEventListener('keydown', handleKeyDown);

        const viewport = map.getViewport();
        viewport.addEventListener('contextmenu', handleContextMenu);

        // Guardar los handlers para eliminarlos después
        drawRef.current._cleanup = () => {
            window.removeEventListener('keydown', handleKeyDown);
            viewport.removeEventListener('contextmenu', handleContextMenu);
        };

        modify.setActive(true);
        map.addInteraction(draw);
        ensureLayerZIndex();
    }


    function desactivarToolMeasurePolygon() {
        if (!map) return;

        if (drawRef.current) {
            if (drawRef.current._cleanup) drawRef.current._cleanup(); // 🔴 Limpieza
            map.removeInteraction(drawRef.current);
            drawRef.current = null;
        }

        sourceMeasurePolygon.clear();
        map.removeLayer(vectorMeasurePolygon);
    }

    function activarToolMeasurePolygon() {
        if (!map) return; // Verificar si map está disponible

        addInteraction('Polygon'); // Cambiar a Polygon
        ensureLayerZIndex();
    }

    useEffect(() => {
        if (toolStates?.measurePolygon?.active) {
            activarToolMeasurePolygon();
        } else {
            desactivarToolMeasurePolygon();
        }

        return () => {
            desactivarToolMeasurePolygon();
        }
    }, [toolStates?.measurePolygon?.active, map]);

    return (
        <Tool
            id="measurePolygon"
            type="toggleable"
            icon='bi bi-bounding-box'
            label={t('Polygon measurement')}
        />
    );
}

export default MeasurePolygon;